<template>
  <div>
    <div v-for="node in nodeData" :key="node.id" class="flex flex-col">
      <div
        class="min-h-[24px] gap-8 items-center flex cursor-pointer"
        @click="toggleExpand(node.id)"
      >
        <div class="w-12 flex">
          <FontAwesomeIcon
            v-if="nodeHasChildren(node)"
            :icon="['fak', 'chevron-right']"
            :style="{ height: '12px', width: '12px', color: 'currentColor' }"
            class="bitts-tree__chevron"
            :class="{
              'text-neutral-accent': !isExpanded(node.id),
              'rotate-90': isExpanded(node.id),
            }"
          />
        </div>
        <slot
          name="node-checkbox"
          :node="node"
          :checked="isIndeterminate(node) ? false : node.checked"
          :indeterminate="isIndeterminate(node)"
          :on-input="(checked: boolean) => onCheckboxChange(checked, node)"
          :disabled="node.isDisabled || isDisabled"
        >
          <BittsCheckbox
            :checked="isIndeterminate(node) ? false : node.checked"
            :indeterminate="isIndeterminate(node)"
            @input="(checked) => onCheckboxChange(checked, node)"
            @click.stop
            :disabled="node.isDisabled || isDisabled"
          />
        </slot>
        <slot name="node-prefix" :node="node"></slot>
        <slot name="node-content" :node="node">
          <p class="text-neutral-text-strong text-base">{{ node.label }}</p>
        </slot>
        <slot name="node-suffix" :node="node"></slot>
      </div>
      <div
        v-if="isExpanded(node.id)"
        class="ml-28 pl-4 border-l border-neutral-border flex flex-col"
      >
        <BittsBaseTree
          v-if="nodeHasChildren(node)"
          :is-disabled
          :node-data="node.children ?? []"
          :get-node-indeterminate="getNodeIndeterminate"
          @update="
            (updatedNode, updatedNodeId) =>
              handleChildUpdate(updatedNode, node, updatedNodeId)
          "
          :expand-all="expandAll"
        >
          <template #node-checkbox="slotProps">
            <slot name="node-checkbox" v-bind="slotProps" />
          </template>
          <template #node-prefix="{ node: nodeSlot }">
            <slot name="node-prefix" :node="nodeSlot" />
          </template>
          <template #node-content="{ node: nodeSlot }">
            <slot name="node-content" :node="nodeSlot">
              <p class="text-neutral-text-strong text-base">{{
                nodeSlot.label
              }}</p>
            </slot>
          </template>
          <template #node-suffix="{ node: nodeSlot }">
            <slot name="node-suffix" :node="nodeSlot"></slot>
          </template>
        </BittsBaseTree>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts" generic="TMetaData">
import { ref } from 'vue';

import BittsCheckbox from '../BittsCheckbox/BittsCheckbox.vue';

export type TreeNodeType<TMetaData = unknown> = {
  label: string;
  checked?: boolean;
  indeterminate?: boolean;
  children?: TreeNodeType<TMetaData>[];
  id: number | string;
  isDisabled?: boolean;
  metaData?: TMetaData;
};

type Node = TreeNodeType<TMetaData>;

const {
  expandAll = false,
  isDisabled = false,
  initialExpandedNodes = {},
  nodeData,
  getNodeIndeterminate,
} = defineProps<{
  isDisabled?: boolean;
  nodeData: Node[];
  expandAll?: boolean;
  initialExpandedNodes?: Record<number | string, boolean>;
  getNodeIndeterminate?: (node: Node) => boolean;
}>();

const emit = defineEmits<{
  update: [value: Node[], updatedNodeId: number | string];
}>();

defineSlots<{
  'node-checkbox'?(props: {
    node: Node;
    checked: boolean | undefined;
    indeterminate: boolean;
    onInput: (checked: boolean) => void;
    disabled: boolean;
  }): unknown;
  'node-prefix'?(props: { node: Node }): unknown;
  'node-content'?(props: { node: Node }): unknown;
  'node-suffix'?(props: { node: Node }): unknown;
}>();

const expandedNodes =
  ref<Record<number | string, boolean>>(initialExpandedNodes);

const nodeHasChildren = (node: Node) => {
  return !!node.children && node.children.length > 0;
};

const isExpanded = (id: number | string) => {
  return expandAll || expandedNodes.value[id] || false;
};

const toggleExpand = (id: number | string) => {
  expandedNodes.value[id] = !expandedNodes.value[id];
};

const isIndeterminate = (node: Node): boolean => {
  if (getNodeIndeterminate) return getNodeIndeterminate(node);
  if (node.indeterminate !== undefined) return node.indeterminate;
  const children = node.children || [];
  if (!children.length) return false;
  const someChecked = children.some((child) => child.checked);
  const someIndeterminate = children.some((child) => child.indeterminate);
  return !node.checked && (someChecked || someIndeterminate);
};

const onEmitUpdate = (updatedNodes: Node[], updatedNodeId: number | string) => {
  emit('update', updatedNodes, updatedNodeId);
};

const onCheckboxChange = (checked: boolean, node: Node) => {
  const updatedNode = {
    ...node,
    checked,
    children: updateChildrenCheck(node.children, checked),
  };
  handleNodeUpdate(updatedNode, node.id);
};

const updateChildrenCheck = (
  children: Node[] | undefined,
  checked: boolean,
): Node[] | undefined => {
  if (!children) return undefined;

  return children.map((child) => ({
    ...child,
    checked,
    children: updateChildrenCheck(child.children, checked),
  }));
};

const handleNodeUpdate = (
  updatedNode: Node,
  updatedNodeId: number | string,
) => {
  const updatedNodeData = nodeData.map((node) =>
    node.id === updatedNode.id ? updatedNode : node,
  );
  onEmitUpdate(updatedNodeData, updatedNodeId);
};

const handleChildUpdate = (
  updatedChildren: Node[],
  parent: Node,
  updatedNodeId: number | string,
) => {
  const updatedParent = {
    ...parent,
    children: updatedChildren,
    checked: updatedChildren.every((child) => child.checked),
  };
  handleNodeUpdate(updatedParent, updatedNodeId);
};
</script>

<style scoped lang="pcss">
@reference "../../../../apps/crossbeam-app/src/css/theme.css";
.bitts-tree__chevron {
  @apply ml-4 transition-transform duration-150;
}
</style>
